import { createClient } from '@supabase/supabase-js';
import { Schedule, Client, Feedback, InventoryItem, ScheduleNote } from '../types';

export const supabase = createClient(
    process.env.REACT_APP_SUPABASE_URL!,
    process.env.REACT_APP_SUPABASE_ANON_KEY!
);

// ─── Schedule ───────────────────────────────────────────
export const toSchedule = (row: any): Schedule => ({
    id: row.id,
    date: row.date,
    timeSlot: { start: '', end: '' },
    type: row.type,
    status: row.status,
    clientId: row.client_id || '',
    ...(row.machine_id ? { machineId: row.machine_id } : {}),
    location: row.location,
    address: row.address || '',
    memo: row.memo,
    assignedTo: row.assigned_to || [],
    createdAt: row.created_at,
});

export const fromSchedule = (s: Schedule) => ({
    id: s.id,
    date: s.date,
    type: s.type,
    status: s.status,
    client_id: s.clientId || '',
    machine_id: s.machineId || null,
    location: s.location,
    address: s.address || '',
    memo: s.memo,
    assigned_to: s.assignedTo,
    created_at: s.createdAt,
});

// ─── Client ─────────────────────────────────────────────
export const toClient = (row: any): Client => ({
    id: row.id,
    name: row.name,
    type: row.type,
    contact: {
        phone: row.phone || '',
        email: row.email || '',
        address: row.address || '',
    },
    managers: row.managers || [],
    machines: [],
    transactionHistory: [],
    outstandingAmount: row.outstanding_amount || 0,
    ...(row.last_payment_date ? { lastPaymentDate: row.last_payment_date } : {}),
    notes: row.notes || '',
    priority: row.priority || '일반',
});

export const fromClient = (c: Client) => ({
    id: c.id,
    name: c.name,
    type: c.type,
    phone: c.contact.phone,
    email: c.contact.email,
    address: c.contact.address,
    managers: c.managers,
    outstanding_amount: c.outstandingAmount,
    last_payment_date: c.lastPaymentDate || null,
    notes: c.notes,
    priority: c.priority,
});

// ─── Feedback ────────────────────────────────────────────
export const toFeedback = (row: any): Feedback => ({
    id: row.id,
    scheduleId: row.schedule_id,
    machineId: row.machine_id || '',
    workDetails: row.work_details || { type: '', description: '', issuesFound: '', resolution: '' },
    sitePhotos: row.site_photos || [],
    condition: row.condition,
    partsUsed: row.parts_used || [],
    customerSignature: row.customer_signature || '',
    technicianSignature: row.technician_signature || '',
    laborCost: row.labor_cost || 0,
    partsCost: row.parts_cost || 0,
    totalCost: row.total_cost || 0,
    paymentMethod: row.payment_method,
    ...(row.next_maintenance ? { nextMaintenance: row.next_maintenance } : {}),
    followUpNeeded: row.follow_up_needed || false,
    submittedAt: row.submitted_at,
});

export const fromFeedback = (f: Feedback) => ({
    id: f.id,
    schedule_id: f.scheduleId,
    machine_id: f.machineId || '',
    work_details: f.workDetails,
    site_photos: f.sitePhotos,
    condition: f.condition,
    parts_used: f.partsUsed,
    customer_signature: f.customerSignature,
    technician_signature: f.technicianSignature,
    labor_cost: f.laborCost,
    parts_cost: f.partsCost,
    total_cost: f.totalCost,
    payment_method: f.paymentMethod,
    next_maintenance: f.nextMaintenance || null,
    follow_up_needed: f.followUpNeeded,
});

// ─── InventoryItem ───────────────────────────────────────
export const toInventoryItem = (row: any): InventoryItem => ({
    id: row.id,
    name: row.name,
    category: row.category,
    quantity: row.quantity || 0,
    unit: row.unit,
    minQuantity: row.min_quantity || 0,
    notes: row.notes || '',
});

// ─── ScheduleNote ────────────────────────────────────────
export const toScheduleNote = (row: any): ScheduleNote => ({
    id: row.id,
    scheduleId: row.schedule_id,
    content: row.content,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
});

export const fromScheduleNote = (n: ScheduleNote) => ({
    id: n.id,
    schedule_id: n.scheduleId,
    content: n.content,
    created_at: n.createdAt,
    updated_at: n.updatedAt,
});

export const fromInventoryItem = (item: InventoryItem) => ({
    id: item.id,
    name: item.name,
    category: item.category,
    quantity: item.quantity,
    unit: item.unit,
    min_quantity: item.minQuantity,
    notes: item.notes,
});
